import BacktestLoader from './components/BacktestLoader';
import EquityChart from './components/EquityChart';
import { useState } from 'react';



export default function App() {
  return (
    <div className="page">
      <BacktestLoader />

      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      {/* ================= 1) HERO / ABOVE THE FOLD ================= */}
      <section className="heroFull">
        <div className="inner">
          <header className="hero heroWide">
            <div className="heroTop">
              <div className="brandPill">SPX Gamma</div>
              <div className="heroMeta">
                ARGOS • Multi-Day • Adaptive • Regime-Guided • Defined Risk
              </div>
            </div>

            <h1 className="heroTitle">
              ARGOS — The Adaptive Regime Guided Option Selling Strategy
            </h1>

            <p className="heroSubtitle">
              A selective, regime-aware SPX options strategy built to extend the SPX Gamma premium-selling
              framework beyond 0-DTE and 1-DTE trades.
            </p>

            <div className="heroBody">
              <p className="heroLead">
                <strong>ARGOS</strong> is the multi-day adaptive component of the broader
                <strong> SPX Gamma Premium Sellers</strong> strategy suite. Where MEIC focuses on
                0-DTE iron condors, GEMIC provides daily 1-DTE iron condor opportunities, and ACCE
                selectively targets 1-DTE dislocations, ARGOS is designed for longer expiration windows
                where trade structure and duration must adapt to the current market environment.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                ARGOS does not issue trades every day. It first evaluates SPX market structure through
                gamma exposure, volatility pressure, regime classification, expected movement, and
                historical forward outcomes. Only when the data supports a defined-risk premium position
                does the strategy select the appropriate structure, direction, and holding period.
              </p>

              <div style={{ height: 5 }} />

              <p className="heroLead">
                Because ARGOS operates across multi-day windows, it creates a more diversified layer
                inside the larger system. A bullish put credit spread and a neutral iron condor may exist
                at the same time because each trade is based on the market structure present when it was
                opened. A new environment does not automatically invalidate a prior position — each trade
                is governed by its own forward distribution, expiration window, and defined-risk profile.
              </p>
            </div>

            <div className="heroActions">
              <a className="btnPrimary" href="#performance">
                View Performance
              </a>
              <a
                className="btnGhost"
                href="https://www.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Access SPX Gamma
              </a>
            </div>
          </header>
        </div>
      </section>

      {/* ================= 2) QUICK IDENTITY STRIP ================= */}
      {/* ================= 2) QUICK IDENTITY STRIP ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="paperStrip">
            <div className="stripItem">
              <div className="stripCallout">Role In The System</div>
              <p className="text">
                ARGOS is the multi-day adaptive layer inside the broader
                <strong> SPX Gamma Premium Sellers</strong> framework. It extends the system beyond
                MEIC’s 0-DTE trades, GEMIC’s daily 1-DTE iron condors, and ACCE’s selective 1-DTE
                opportunities.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Diversification Layer</div>
              <p className="text">
                SPX Gamma only trades SPX, so diversification comes from strategy design rather than
                trading multiple symbols. ARGOS adds a longer-duration component that can work alongside
                the shorter-dated systems to help smooth results across different market conditions.
              </p>
            </div>

            <div className="stripItem">
              <div className="stripCallout">Adaptive Trade Selection</div>
              <p className="text">
                ARGOS does not force the same position every day. It first identifies the current market
                environment — <strong>bull</strong>, <strong>bear</strong>, <strong>neutral</strong>,
                <strong> unstable</strong>, <strong>bearish exhaustion</strong>, or
                <strong> bullish exhaustion</strong> — then determines whether the opportunity supports
                a bullish, bearish, neutral, reversal-based, defensive, or no-trade structure.
              </p>
            </div>

            <div className="stripCallout">
              ARGOS is built to add consistency through diversification. By combining multiple SPX
              premium-selling strategies across different trade durations, structures, and market
              environments, the broader framework can help reduce portfolio drawdowns during weaker
              conditions while maximizing profitability when structure aligns.
            </div>
          </div>
        </div>
      </section>

      {/* ================= 3) PERFORMANCE ================= */}
      <section id="performance" className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Performance</h2>
          </div>

          <div className="lede">
            <p className="text">
              ARGOS performance is evaluated as a full adaptive strategy, not as a single fixed
              trade type. The system changes structure based on market environment, forward
              distribution behavior, and regime-specific edge. This performance section is reserved
              for the live and backtested equity curve.
            </p>
          </div>

          <div className="feature">
            <div className="featureBody">
              <EquityChart />
            </div>
          </div>
        </div>
      </section>

      {/* ================= 4) STRATEGY OVERVIEW ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Overview</h2>
          </div>

          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">What ARGOS Does</h3>

              <p className="text">
                ARGOS is SPX Gamma’s adaptive premium-selling strategy for SPX options.
                Each trading day, shortly after the market opens, the ARGOS program reads
                the current market condition and determines whether a new position should
                be placed. ARGOS does not place trades every day, and it does not use price alone as
                the guide. The strategy is designed to identify meaningful changes in market
                structure and issue trades only when the current environment supports a new
                defined-risk position.
              </p>

              <div className="stripCallout">
                The goal is simple: sell premium only when market structure supports it,
                and match the position to the current market environment.
              </div>
            </div>
          </div>

          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">How ARGOS Makes A Decision</h3>

              <p className="text">
                After the market opens, ARGOS evaluates the current SPX environment using
                options flow, gamma exposure, volatility conditions, regime behavior, and
                historical forward movement. It then determines whether market structure has
                changed enough to justify a new position.
              </p>

              <div className="grid2">
                <div className="paperCard">
                  <div className="paperHead">
                    <div className="paperTitle">Options Flow</div>
                    <div className="paperTag">Market Pressure</div>
                  </div>
                  <p className="text">
                    ARGOS evaluates current options flow to understand whether pressure is
                    building to the upside, downside, or remaining balanced.
                  </p>
                </div>

                <div className="paperCard">
                  <div className="paperHead">
                    <div className="paperTitle">Gamma Exposure</div>
                    <div className="paperTag">Dealer Structure</div>
                  </div>
                  <p className="text">
                    Gamma exposure helps identify whether the market is more likely to behave
                    in a controlled or unstable manner.
                  </p>
                </div>

                <div className="paperCard">
                  <div className="paperHead">
                    <div className="paperTitle">Regime & Volatility</div>
                    <div className="paperTag">Market Environment</div>
                  </div>
                  <p className="text">
                    ARGOS evaluates the current regime and volatility environment to determine
                    whether conditions favor bullish, bearish, neutral, unstable, or exhaustion
                    setups.
                  </p>
                </div>

                <div className="paperCard">
                  <div className="paperHead">
                    <div className="paperTitle">Forward Movement</div>
                    <div className="paperTag">Historical Behavior</div>
                  </div>
                  <p className="text">
                    The strategy compares the current setup to similar historical conditions
                    to determine which position structure and expiration window best fit the
                    environment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="paperLong">
            <div className="paperBlock">
              <h3 className="subTitle">Trade Timing And Risk</h3>

              <p className="text">
                When a suitable trading situation is identified, ARGOS issues the trade
                approximately one minute after the market opens. In the backtest, positions were
                held to OPEX to create a consistent, rules-based evaluation framework. Holding to
                expiration remains a viable approach, but more active traders may choose to manage
                positions earlier based on their own risk tolerance, profit targets, and portfolio
                objectives. Each position requires approximately $2,000 in capital per contract. The
                number of open positions is capped to limit total exposure. When trading
                one contract per position, the total capital at risk at any one time is
                limited to $10,000.
              </p>


              <div className="stripCallout">
                ARGOS is designed to reduce the time and effort required from the trader:
                wait for the signal, enter the position if issued, and hold to OPEX.
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ================= 5) MARKET CONDITIONS ================= */}
      <section className="sectionFull sectionBand">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Market Conditions And Position Types</h2>
            <p className="text">
              ARGOS classifies SPX into six market conditions. Each condition is matched
              with a specific 20-point wide SPX options structure.
            </p>
          </div>

          <div className="feature">
            <div className="featureBody">
              <figure className="figure">
                <img
                  className="figureImg argosConditionImg"
                  src="/argos-market-conditions.png"
                  alt="ARGOS market conditions and position types"
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 10) MONTE CARLO VALIDATION ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Monte Carlo Validation</h2>
            <p className="text">
              ARGOS was tested across 10,000 randomized trade-sequence simulations using a
              $20,000 starting portfolio. The goal was to determine whether the strategy remains
              durable when wins, losses, and drawdowns occur in different orders.
            </p>
          </div>

          <div className="split split--mc">
            <div className="splitLeft">
              <p className="text">
                Because ARGOS is a multi-day adaptive strategy, the order of outcomes matters.
                A strong backtest can look attractive in one historical sequence, but Monte Carlo
                testing shows how the same trade profile behaves across thousands of possible
                paths.
              </p>

              <p className="text">
                The results show strong durability. Across 10,000 simulations, the median ending
                portfolio value was approximately <strong>$99,994</strong>, turning a
                <strong> $20,000</strong> starting portfolio into roughly a
                <strong> 5x median outcome</strong>. The mean ending portfolio was approximately
                <strong> $99,827</strong>, showing that the typical simulation remained closely
                aligned with the median result.
              </p>

              <div className="callout soft">
                <div className="mutedLine">
                  <strong>Starting Portfolio:</strong> $20,000
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Simulations:</strong> 10,000 randomized paths
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Median Ending Portfolio:</strong> $99,994
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Mean Ending Portfolio:</strong> $99,827
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>5th Percentile Ending Portfolio:</strong> $71,290
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>95th Percentile Ending Portfolio:</strong> $127,493
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Best Simulation:</strong> $169,134
                </div>

                <div style={{ height: 10 }} />

                <div className="mutedLine">
                  <strong>Blowup Rate:</strong> 1 out of 10,000 simulations, or 0.01%
                </div>
              </div>

              <div style={{ height: 10 }} />

              <p className="text">
                Drawdowns were present, but generally survivable. The median maximum drawdown was
                approximately <strong>-$8,111</strong>, or <strong>-18.6%</strong>. The 10th
                percentile max drawdown was approximately <strong>-33.8%</strong>, while the 5th
                percentile max drawdown was approximately <strong>-40.3%</strong>.
              </p>

              <p className="stripCallout">
                The Monte Carlo takeaway is clear: ARGOS is not dependent on one favorable backtest
                sequence. Across randomized trade ordering, the strategy showed strong median growth,
                controlled lower-tail outcomes, and an extremely low failure rate.
              </p>
            </div>

            <div className="splitRight">
              <figure className="figure">
                <img
                  className="figureImg"
                  src="/MC Path Final.png"
                  alt="ARGOS Monte Carlo equity paths"
                  style={{ width: '100%' }}
                />

                <figcaption className="stripCallout">
                  Monte Carlo equity paths for ARGOS using 10,000 randomized trade-sequence simulations.
                  The chart shows how the strategy performs across favorable, typical, and adverse
                  ordering of trade outcomes.
                </figcaption>

                <div className="figureDisclaimer">
                  Historical and simulated results are shown for illustrative purposes only and do not
                  guarantee future returns.
                </div>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 11) RISK MANAGEMENT PHILOSOPHY ================= */}
      <section className="sectionFull sectionBand">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Risk Management Philosophy</h2>
          </div>

          <div className="grid2">
            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Defined Risk First</div>
                <div className="paperTag">Known Exposure</div>
              </div>

              <p className="text">
                ARGOS is built around defined-risk options structures. Every trade is designed
                with known max risk and known max reward before entry. This is critical because
                SPX can move aggressively, especially during unstable or expansion environments.
              </p>




            </div>

            <div className="paperCard">
              <div className="paperHead">
                <div className="paperTitle">Selective Exposure</div>
                <div className="paperTag">No Overtrading</div>
              </div>

              <p className="text">
                ARGOS does not try to eliminate losses. No options strategy can do that. Instead,
                the framework aims to avoid the worst environments, size exposure appropriately,
                and focus only on repeatable, data-supported setups.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 3) STRATEGY PERFORMANCE ================= */}
      <section id="performance" className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Strategy Performance</h2>
            <p className="text">
              ARGOS was tested from <strong>January 13, 2022</strong> through
              <strong> April 14, 2026</strong>, covering <strong>1,109 trading days</strong>.
              Over that period, the strategy produced <strong>365 total trades</strong> and grew
              a <strong>$20,000</strong> starting portfolio to <strong>$98,721</strong>.
            </p>
          </div>

          <div className="kpiGrid">
            <div className="kpiCard">
              <div className="kpiLabel">Starting Portfolio</div>
              <div className="kpiValue">$20,000</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Ending Portfolio</div>
              <div className="kpiValue">$98,721</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Net Profit</div>
              <div className="kpiValue">+$78,721</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Total Return</div>
              <div className="kpiValue">+393.6%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">CAGR</div>
              <div className="kpiValue">45.6%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Total Trades</div>
              <div className="kpiValue">365</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Profitable Days</div>
              <div className="kpiValue">258</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Losing Days</div>
              <div className="kpiValue">78</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Active-Day Win Rate</div>
              <div className="kpiValue">76.8%</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">All-Day Win Rate</div>
              <div className="kpiValue">23.3%</div>
              <div className="kpiSub">Includes no-trade days</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Average Active-Day P/L</div>
              <div className="kpiValue">+$234</div>
            </div>

            <div className="kpiCard">
              <div className="kpiLabel">Median Active-Day P/L</div>
              <div className="kpiValue">+$555</div>
            </div>
          </div>
          <div className="grid2" style={{ marginTop: 22 }}>
            <div className="paperCard">
              <div className="paperHead">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    width: '100%',
                  }}
                >
                  <div className="paperTitle">Risk & Drawdown Profile</div>
                  <div className="paperTag">Capital preservation during weak sequences</div>
                </div>
              </div>

              <div className="kpiGrid" style={{ marginTop: 14 }}>
                <div className="kpiCard">
                  <div className="kpiLabel">Max Drawdown</div>
                  <div className="kpiValue">-$10,600</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Max Drawdown %</div>
                  <div className="kpiValue">-18.9%</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Drawdown Duration</div>
                  <div className="kpiValue">144 Days</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Return / Max DD</div>
                  <div className="kpiValue">20.88</div>
                </div>
              </div>

              <p className="text" style={{ marginTop: 14 }}>
                The largest historical drawdown was <strong>-$10,600</strong>, or approximately
                <strong> -18.9%</strong>. The longest drawdown period lasted
                <strong> 144 trading days</strong>. Despite that drawdown window, the strategy
                finished with a <strong>20.88 return-to-max-drawdown ratio</strong>.
              </p>
            </div>

            <div className="paperCard">
              <div className="paperHead">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    width: '100%',
                  }}
                >
                  <div className="paperTitle">Risk-Adjusted Performance</div>
                  <div className="paperTag">Return quality, not just total profit</div>
                </div>
              </div>

              <div className="kpiGrid" style={{ marginTop: 14 }}>
                <div className="kpiCard">
                  <div className="kpiLabel">Sharpe Ratio</div>
                  <div className="kpiValue">2.03</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Sortino Ratio</div>
                  <div className="kpiValue">1.21</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Calmar Ratio</div>
                  <div className="kpiValue">2.42</div>
                </div>

                <div className="kpiCard">
                  <div className="kpiLabel">Annualized Volatility</div>
                  <div className="kpiValue">18.7%</div>
                </div>
              </div>

              <p className="text" style={{ marginTop: 14 }}>
                ARGOS produced a <strong>45.6% CAGR</strong> with annualized volatility of
                <strong> 18.7%</strong>. The <strong>2.03 Sharpe</strong>,
                <strong> 1.21 Sortino</strong>, and <strong>2.42 Calmar</strong> show that
                the strategy generated strong returns relative to volatility and drawdown.
              </p>
            </div>
          </div>



          <div className="stripCallout" style={{ marginTop: 22 }}>
            ARGOS grew the portfolio from <strong>$20,000</strong> to <strong>$98,721</strong>,
            producing <strong>+$78,721</strong> in net profit with a <strong>45.6% CAGR</strong>,
            a <strong>76.8% active-day win rate</strong>, and a maximum drawdown of
            <strong> -18.9%</strong>.
          </div>
        </div>
      </section>



      {/* ================= 14) FINAL CTA ================= */}
      {/* ================= 14) FINAL CTA ================= */}
      <section className="sectionFull">
        <div className="inner">
          <div className="sectionHeader">
            <h2 className="sectionTitle">Adaptive Premium Selling For Real Market Structure</h2>
          </div>

          <div className="paperCard">
            <p className="text">
              <strong>ARGOS</strong> is the adaptive multi-day premium-selling layer inside the broader{' '}
              <strong>SPX Gamma Premium Sellers suite</strong>. It extends the framework beyond static daily trade ideas by
              adapting structure, direction, and duration to the current SPX market environment.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              The strategy is designed to read the current SPX market structure, classify the environment, determine whether
              a trade is justified, select the appropriate premium-selling structure, choose the holding period, and use
              historical forward distributions to guide strike placement.
            </p>

            <p className="text" style={{ marginTop: 10 }}>
              ARGOS gives the suite a way to participate when the best opportunity is not a 0-DTE or 1-DTE setup, but a
              multi-day structure aligned with regime behavior, volatility pressure, dealer positioning, and forward price
              distribution.
            </p>

            <div className="heroActions" style={{ marginTop: 16 }}>
              <a
                className="btnPrimary"
                href="https://www.spxgamma.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore ARGOS
              </a>

              <a
                className="btnGhost"
                href="#performance"
              >
                Study the Performance
              </a>
            </div>

            <div className="figureDisclaimer" style={{ marginTop: 16 }}>
              ARGOS trade posts are for educational and informational purposes only. They are not financial advice or a
              recommendation to buy or sell any security or options contract. Options trading involves risk and may not be
              suitable for all traders. Members are responsible for their own trade decisions, position sizing, and risk
              management.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}







